import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Play, RotateCcw, Home, ChevronRight, Sparkles, Volume2, VolumeX, Pause, Rocket } from 'lucide-react';
import {
  LEVELS, MAX_LEVEL, biomeForLevel, SHIPS, shipById, computeStars,
} from '@/lib/peaceDefenderLevels';
import {
  recordRun, playSfx, isSfxMuted, setSfxMuted,
  getPD, savePDLevelResult, savePDEndless,
  unlockPDShip, equipPDShip,
  subscribe, getMeta,
} from '@/lib/loungeMeta';
import PeaceDefenderLevelSelect from './lounge/PeaceDefenderLevelSelect';
import StarBadge from './lounge/StarBadge';
import { useLoungeCountdown, LoungeCountdown } from '@/lib/loungeGame';

/* -------------------- Constants -------------------- */
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand  = (a, b) => a + Math.random() * (b - a);
const TAU   = Math.PI * 2;

// Asteroid tiers: mass points → radius + hits-to-destroy
const ROCK_TIERS = {
  3: { radius: 34, hp: 3, mass: 3, splitInto: [2] },
  2: { radius: 22, hp: 2, mass: 2, splitInto: [1, 1] },
  1: { radius: 12, hp: 1, mass: 1, splitInto: [] },
};

/* ============================================================================
 * Peace Defender — 30-level neon Asteroids-style Lounge game
 * ============================================================================ */
export default function PeaceDefender() {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const sectionRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const stateRef = useRef(null);
  const pausedRef = useRef(false);
  // Input refs — updated by pointer/keyboard handlers, read every frame.
  const inputRef = useRef({ rotL: false, rotR: false, thrust: false, fire: false });

  // Views: map | ready | playing | over
  const [view, setView] = useState('map');
  const [pd, setPd] = useState(() => getPD());
  const [levelId, setLevelId] = useState(() => Math.min(getPD().lastLevel || 1, 30));
  const [mode, setMode] = useState('campaign'); // campaign | endless | master
  const [muted, setMuted] = useState(() => isSfxMuted());
  const [fp, setFp] = useState(() => getMeta().fp || 0);
  useEffect(() => subscribe(() => {
    setPd(getMeta().pd);
    setMuted(isSfxMuted());
    setFp(getMeta().fp || 0);
  }), []);

  // HUD state
  const [hudScore, setHudScore] = useState(0);
  const [hudLives, setHudLives] = useState(3);
  const [hudRemaining, setHudRemaining] = useState(0);
  const [paused, setPaused] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [notice, setNotice] = useState(null); // toast for hangar unlocks
  const { countdown, beginCountdown, cancelCountdown } = useLoungeCountdown();

  const level = useMemo(() => LEVELS[levelId - 1], [levelId]);
  const biome = useMemo(() => biomeForLevel(levelId), [levelId]);
  const ship  = useMemo(() => shipById(pd?.hangar?.currentShip || 'arrow'), [pd]);
  const bestStars = pd?.levelStars?.[levelId] ?? 0;

  const scrollSectionIntoView = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }));
  };

  /* ---- Canvas resize ---- */
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapperRef.current;
    if (!canvas || !wrap) return;
    const w = Math.min(760, wrap.clientWidth);
    let h = Math.max(360, Math.min(480, Math.round(w * 0.68)));
    // Full-screen embed (native WebView): grow to fill the viewport height.
    if (canvas.closest('[data-embed]')) {
      const top = wrap.getBoundingClientRect().top;
      const avail = Math.round((window.visualViewport?.height || window.innerHeight) - top - 12);
      if (avail > h) h = Math.min(avail, 1000);
    }
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    return { w, h };
  }, []);

  /* ---- Build fresh play state ---- */
  const buildState = useCallback((W, H, cfg, mo, shipCfg) => {
    const startingLives = (cfg.lives ?? 3) + (shipCfg.bonusLives || 0);
    const rocks = [];
    // Compose rocks from wave-mass budget.
    let budget = cfg.waveMass;
    if (cfg.isBoss && mo === 'campaign') {
      // Boss level: one giant (mass 3, larger radius/hp) plus a few mediums to keep pressure
      const boss = spawnRock(W, H, 3, cfg.rockSpeed * 0.65, true);
      rocks.push(boss);
      budget -= 5; // boss "costs" 5 mass units
    }
    while (budget > 0) {
      const t = budget >= 3 ? 3 : budget >= 2 ? 2 : 1;
      rocks.push(spawnRock(W, H, t, cfg.rockSpeed, false));
      budget -= t;
    }
    // Cache stars for parallax
    const stars = Array.from({ length: 60 }, () => ({
      x: rand(0, W), y: rand(0, H),
      s: rand(0.6, 2.2),
      tw: rand(0, TAU),
    }));

    return {
      W, H,
      cfg, mode: mo,
      // Ship
      x: W * 0.5, y: H * 0.5,
      vx: 0, vy: 0,
      angle: -Math.PI / 2,
      thrusting: false,
      // Ship visuals + physics from ship config
      ship: shipCfg,
      fireT: 0,
      shieldT: 0,      // seconds of shield remaining
      rapidT: 0,       // seconds of rapid-fire remaining
      invulT: 1.5,     // opening invulnerability so player can orient
      // Progression
      score: 0,         // asteroid kills
      tokens: 0,        // power-ups picked
      hits: 0,          // damage taken
      lives: startingLives,
      startingLives,
      cleared: false,
      gameOver: false,
      // Entities
      rocks,
      bullets: [],
      scouts: [],
      scoutT: cfg.hasScouts ? rand(6, 10) : Infinity,
      scoutsSpawned: 0,
      powerUps: [],
      powerUpT: cfg.powerUpEvery,
      // FX
      particles: [],
      shakeT: 0,
      // Background
      stars,
      parallaxT: 0,
    };
    // Init helper — deps intentionally empty; spawnRock is a module-level fn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- Spawn helpers ---- */
  function spawnRock(W, H, tier, baseSpeed, isBoss) {
    const t = ROCK_TIERS[tier];
    // Spawn on edge, drift inward
    const edge = Math.floor(rand(0, 4));
    let x, y;
    if (edge === 0)      { x = -t.radius;      y = rand(0, H); }
    else if (edge === 1) { x = W + t.radius;   y = rand(0, H); }
    else if (edge === 2) { x = rand(0, W);     y = -t.radius; }
    else                 { x = rand(0, W);     y = H + t.radius; }
    const targetX = rand(W * 0.25, W * 0.75);
    const targetY = rand(H * 0.25, H * 0.75);
    const dx = targetX - x, dy = targetY - y;
    const dist = Math.max(1, Math.hypot(dx, dy));
    const speed = baseSpeed * (isBoss ? 0.55 : rand(0.75, 1.2));
    return {
      x, y,
      vx: (dx / dist) * speed / 60,
      vy: (dy / dist) * speed / 60,
      radius: isBoss ? t.radius * 1.6 : t.radius,
      hp: isBoss ? 8 : t.hp,
      tier,
      isBoss: !!isBoss,
      angle: rand(0, TAU),
      spin: rand(-0.02, 0.02),
      // Deterministic craggy shape (n vertices with radius jitter)
      shape: buildRockShape(),
    };
  }

  function buildRockShape() {
    const n = 8 + Math.floor(rand(0, 4));
    const pts = [];
    for (let i = 0; i < n; i++) {
      pts.push(0.75 + Math.random() * 0.4);
    }
    return pts;
  }

  function spawnScout(s) {
    const W = s.W, H = s.H;
    const fromLeft = Math.random() < 0.5;
    return {
      x: fromLeft ? -30 : W + 30,
      y: rand(H * 0.15, H * 0.7),
      vx: (fromLeft ? 1 : -1) * (s.cfg.rockSpeed * 0.55) / 60,
      vy: 0,
      angle: 0,
      fireT: rand(1.2, 2.2),
      radius: 14,
      hp: 2,
    };
  }

  function spawnPowerUp(s) {
    const kind = Math.random() < 0.5 ? 'shield' : 'rapid';
    return {
      x: rand(s.W * 0.15, s.W * 0.85),
      y: rand(s.H * 0.15, s.H * 0.85),
      kind,
      t: 0,
      radius: 10,
      ttl: 10,
    };
  }

  function spawnParticles(s, x, y, color, count = 10, speed = 1.6) {
    for (let i = 0; i < count; i++) {
      const a = rand(0, TAU);
      const v = rand(speed * 0.4, speed);
      s.particles.push({
        x, y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        life: rand(0.4, 0.9),
        maxLife: 0.9,
        color,
        size: rand(1.5, 3.2),
      });
    }
  }

  /* ---- Main loop ---- */
  const loop = useCallback((ts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const s = stateRef.current;
    if (!s) return;

    if (!lastTsRef.current) lastTsRef.current = ts;
    const dt = Math.min(0.033, (ts - lastTsRef.current) / 1000);
    lastTsRef.current = ts;

    if (!pausedRef.current && !s.gameOver && !s.cleared) {
      update(s, dt);
    }
    render(ctx, s);
    rafRef.current = requestAnimationFrame(loop);
    // Stable RAF loop — reads mutable state via refs (stateRef/pausedRef/lastTsRef).
    // Deps intentionally empty; see FocusDrive.jsx for rationale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update(s, dt) {
    const inp = inputRef.current;

    // Timers
    s.parallaxT += dt;
    s.fireT = Math.max(0, s.fireT - dt * 1000);
    s.shieldT = Math.max(0, s.shieldT - dt);
    s.rapidT  = Math.max(0, s.rapidT  - dt);
    s.invulT  = Math.max(0, s.invulT  - dt);
    if (s.shakeT > 0) s.shakeT = Math.max(0, s.shakeT - dt);

    // Ship rotation
    const rotSpeed = 3.4; // rad/sec
    if (inp.rotL) s.angle -= rotSpeed * dt;
    if (inp.rotR) s.angle += rotSpeed * dt;

    // Thrust
    if (inp.thrust) {
      s.vx += Math.cos(s.angle) * s.ship.thrust;
      s.vy += Math.sin(s.angle) * s.ship.thrust;
      s.thrusting = true;
    } else {
      s.thrusting = false;
    }

    // Drag + speed cap
    s.vx *= 0.988;
    s.vy *= 0.988;
    const spd = Math.hypot(s.vx, s.vy);
    if (spd > s.ship.maxSpeed) {
      s.vx = (s.vx / spd) * s.ship.maxSpeed;
      s.vy = (s.vy / spd) * s.ship.maxSpeed;
    }

    s.x += s.vx;
    s.y += s.vy;
    // Screen wrap
    if (s.x < -20) s.x = s.W + 20; else if (s.x > s.W + 20) s.x = -20;
    if (s.y < -20) s.y = s.H + 20; else if (s.y > s.H + 20) s.y = -20;

    // Firing
    const cooldown = s.rapidT > 0 ? s.ship.fireCooldown * 0.45 : s.ship.fireCooldown;
    if (inp.fire && s.fireT <= 0) {
      fire(s);
      s.fireT = cooldown;
      playSfx('laser', { volume: 0.25 });
    }

    // Update rocks
    for (const r of s.rocks) {
      r.x += r.vx * dt * 60;
      r.y += r.vy * dt * 60;
      r.angle += r.spin;
      if (r.x < -r.radius) r.x = s.W + r.radius;
      else if (r.x > s.W + r.radius) r.x = -r.radius;
      if (r.y < -r.radius) r.y = s.H + r.radius;
      else if (r.y > s.H + r.radius) r.y = -r.radius;
    }

    // Update bullets
    for (const b of s.bullets) {
      b.x += b.vx * dt * 60;
      b.y += b.vy * dt * 60;
      b.life -= dt;
    }
    s.bullets = s.bullets.filter(b => b.life > 0 && b.x > -10 && b.x < s.W + 10 && b.y > -10 && b.y < s.H + 10);

    // Bullet ↔ Rock collision
    for (let i = s.rocks.length - 1; i >= 0; i--) {
      const r = s.rocks[i];
      for (let j = s.bullets.length - 1; j >= 0; j--) {
        const b = s.bullets[j];
        const dx = b.x - r.x, dy = b.y - r.y;
        if (dx * dx + dy * dy < r.radius * r.radius) {
          r.hp -= 1;
          s.bullets.splice(j, 1);
          if (r.hp <= 0) {
            // destroy rock
            s.rocks.splice(i, 1);
            s.score += r.isBoss ? 5 : (r.tier === 3 ? 3 : r.tier === 2 ? 2 : 1);
            setHudScore(s.score);
            spawnParticles(s, r.x, r.y, biome.accent, r.isBoss ? 24 : 12, r.isBoss ? 2.6 : 1.8);
            s.shakeT = Math.max(s.shakeT, r.isBoss ? 0.35 : 0.12);
            playSfx('boom', { volume: 0.32 });
            // Split
            const tier = ROCK_TIERS[r.tier];
            const splits = r.isBoss ? [2, 2, 2, 1, 1] : tier.splitInto;
            for (const st of splits) {
              const t = ROCK_TIERS[st];
              const angle = rand(0, TAU);
              const speed = s.cfg.rockSpeed * rand(0.85, 1.25) / 60;
              s.rocks.push({
                x: r.x, y: r.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                radius: t.radius,
                hp: t.hp,
                tier: st,
                isBoss: false,
                angle: rand(0, TAU),
                spin: rand(-0.04, 0.04),
                shape: buildRockShape(),
              });
            }
          } else {
            spawnParticles(s, b.x, b.y, biome.glow, 3, 1.2);
          }
          break; // one bullet, one rock
        }
      }
    }

    // Ship ↔ Rock collision  (hitbox ~14px scaled to the larger ship)
    if (s.invulT <= 0 && s.shieldT <= 0) {
      for (const r of s.rocks) {
        const dx = s.x - r.x, dy = s.y - r.y;
        const rr = r.radius + 14;
        if (dx * dx + dy * dy < rr * rr) {
          takeDamage(s);
          break;
        }
      }
    }

    // Scouts (spawn + AI + fire)
    if (s.cfg.hasScouts && !s.gameOver && !s.cleared) {
      s.scoutT -= dt;
      if (s.scoutT <= 0 && s.scoutsSpawned < s.cfg.scoutCount) {
        s.scouts.push(spawnScout(s));
        s.scoutsSpawned += 1;
        s.scoutT = rand(5, 10);
      }
      for (let i = s.scouts.length - 1; i >= 0; i--) {
        const sc = s.scouts[i];
        sc.x += sc.vx * dt * 60;
        sc.y += sc.vy * dt * 60 + Math.sin(s.parallaxT * 2 + sc.x * 0.03) * 0.3;
        sc.fireT -= dt;
        if (sc.fireT <= 0) {
          const a = Math.atan2(s.y - sc.y, s.x - sc.x);
          const spd = 3.4;
          s.bullets.push({
            x: sc.x, y: sc.y,
            vx: Math.cos(a) * spd, vy: Math.sin(a) * spd,
            life: 1.8, hostile: true,
          });
          sc.fireT = rand(1.4, 2.6);
          playSfx('laser', { volume: 0.18 });
        }
        // Scout ↔ player bullets
        for (let j = s.bullets.length - 1; j >= 0; j--) {
          const b = s.bullets[j];
          if (b.hostile) continue;
          const dx = b.x - sc.x, dy = b.y - sc.y;
          if (dx * dx + dy * dy < sc.radius * sc.radius) {
            sc.hp -= 1;
            s.bullets.splice(j, 1);
            if (sc.hp <= 0) {
              s.scouts.splice(i, 1);
              s.score += 3;
              setHudScore(s.score);
              spawnParticles(s, sc.x, sc.y, biome.glow, 14, 2.0);
              playSfx('boom', { volume: 0.28 });
            }
            break;
          }
        }
        // Off-screen cleanup
        if (sc.x < -60 || sc.x > s.W + 60) s.scouts.splice(i, 1);
      }
    }

    // Hostile bullets → player
    for (let j = s.bullets.length - 1; j >= 0; j--) {
      const b = s.bullets[j];
      if (!b.hostile) continue;
      const dx = b.x - s.x, dy = b.y - s.y;
      if (dx * dx + dy * dy < 20 * 20) {
        s.bullets.splice(j, 1);
        if (s.invulT <= 0 && s.shieldT <= 0) takeDamage(s);
      }
    }

    // Power-ups
    s.powerUpT -= dt;
    if (s.powerUpT <= 0 && s.powerUps.length < 2) {
      s.powerUps.push(spawnPowerUp(s));
      s.powerUpT = s.cfg.powerUpEvery;
    }
    for (let i = s.powerUps.length - 1; i >= 0; i--) {
      const p = s.powerUps[i];
      p.t += dt;
      p.ttl -= dt;
      const dx = p.x - s.x, dy = p.y - s.y;
      if (dx * dx + dy * dy < (p.radius + 14) * (p.radius + 14)) {
        if (p.kind === 'shield') s.shieldT = 6;
        if (p.kind === 'rapid')  s.rapidT  = 5;
        s.tokens += 1;
        s.powerUps.splice(i, 1);
        spawnParticles(s, p.x, p.y, biome.glow, 12, 1.6);
        playSfx('powerup', { volume: 0.35 });
      } else if (p.ttl <= 0) {
        s.powerUps.splice(i, 1);
      }
    }

    // Particles
    for (const p of s.particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.96;
      p.vy *= 0.96;
      p.life -= dt;
    }
    s.particles = s.particles.filter(p => p.life > 0);

    // HUD state
    if (s.mode === 'campaign') {
      setHudRemaining(s.rocks.length + s.scouts.length);
    } else {
      setHudRemaining(s.rocks.length + s.scouts.length);
    }
    setHudLives(s.lives);

    // Endless: keep spawning rocks
    if (s.mode !== 'campaign' && s.rocks.length < 6) {
      s.rocks.push(spawnRock(s.W, s.H, Math.random() < 0.4 ? 3 : Math.random() < 0.6 ? 2 : 1, s.cfg.rockSpeed, false));
    }

    // Win condition (campaign)
    if (s.mode === 'campaign' && s.rocks.length === 0 && (!s.cfg.hasScouts || (s.scoutsSpawned >= s.cfg.scoutCount && s.scouts.length === 0))) {
      s.cleared = true;
      queueRunOver(s, true);
    }

    // Master mode ends when player dies with 1 life effectively
  }

  function fire(s) {
    const cx = s.x + Math.cos(s.angle) * 22;
    const cy = s.y + Math.sin(s.angle) * 22;
    const speed = 6.8;
    const spread = s.ship.shotCount === 3 ? 0.18 : s.ship.shotCount === 2 ? 0.06 : 0;
    if (s.ship.shotCount === 1) {
      s.bullets.push({ x: cx, y: cy, vx: Math.cos(s.angle) * speed, vy: Math.sin(s.angle) * speed, life: 1.2, hostile: false });
    } else if (s.ship.shotCount === 2) {
      const off = 8;
      const px = -Math.sin(s.angle) * off, py = Math.cos(s.angle) * off;
      s.bullets.push({ x: cx + px, y: cy + py, vx: Math.cos(s.angle) * speed, vy: Math.sin(s.angle) * speed, life: 1.2, hostile: false });
      s.bullets.push({ x: cx - px, y: cy - py, vx: Math.cos(s.angle) * speed, vy: Math.sin(s.angle) * speed, life: 1.2, hostile: false });
    } else {
      for (let i = -1; i <= 1; i++) {
        const a = s.angle + i * spread;
        s.bullets.push({ x: cx, y: cy, vx: Math.cos(a) * speed, vy: Math.sin(a) * speed, life: 1.2, hostile: false });
      }
    }
  }

  function takeDamage(s) {
    s.lives -= 1;
    s.hits  += 1;
    s.invulT = 1.8;
    s.shakeT = 0.4;
    spawnParticles(s, s.x, s.y, '#ff5c5c', 20, 2.4);
    playSfx('hit', { volume: 0.38 });
    if (s.lives <= 0) {
      s.gameOver = true;
      queueRunOver(s, false);
    }
  }

  const queueRunOver = (s, cleared) => {
    // Defer the state update out of the render loop
    setTimeout(() => {
      finalizeRun(s, cleared);
    }, cleared ? 500 : 700);
  };

  const finalizeRun = (s, cleared) => {
    if (view === 'over') return;
    const stars = s.mode === 'campaign'
      ? computeStars(level, { cleared, hits: s.hits, livesLeft: s.lives, startingLives: s.startingLives })
      : 0;
    const result = {
      level: levelId,
      score: s.score,
      tokens: s.tokens,
      hits: s.hits,
      livesLeft: s.lives,
      cleared,
      perfect: cleared && s.hits === 0,
      stars,
      mode: s.mode,
    };
    setRunResult(result);
    setView('over');
    // Persist
    if (s.mode === 'campaign') {
      savePDLevelResult(levelId, { stars, cleared, hits: s.hits, livesLeft: s.lives });
    } else {
      savePDEndless(s.score, s.mode);
    }
    recordRun('defender', result);
    playSfx(cleared ? 'levelClear' : 'collision', { volume: 0.35 });
  };

  /* ---- Rendering ---- */
  function render(ctx, s) {
    const { W, H } = s;
    ctx.save();
    if (s.shakeT > 0) {
      const mag = s.shakeT * 8;
      ctx.translate(rand(-mag, mag), rand(-mag, mag));
    }

    // Background gradient
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, biome.bg[0]);
    g.addColorStop(1, biome.bg[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // Distant nebula haze
    const haze = ctx.createRadialGradient(W * 0.7, H * 0.35, 20, W * 0.7, H * 0.35, W * 0.7);
    haze.addColorStop(0, `${biome.glow}30`);
    haze.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = haze;
    ctx.fillRect(0, 0, W, H);
    const haze2 = ctx.createRadialGradient(W * 0.2, H * 0.75, 20, W * 0.2, H * 0.75, W * 0.6);
    haze2.addColorStop(0, `${biome.accent}22`);
    haze2.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = haze2;
    ctx.fillRect(0, 0, W, H);

    // Stars (twinkle)
    for (const st of s.stars) {
      const tw = 0.5 + 0.5 * Math.sin(s.parallaxT * 2 + st.tw);
      ctx.globalAlpha = 0.35 + tw * 0.5;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(st.x, st.y, st.s, st.s);
    }
    ctx.globalAlpha = 1;

    // Grid lines faint
    ctx.strokeStyle = `${biome.accent}14`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      ctx.moveTo(0, (H / 5) * i);
      ctx.lineTo(W, (H / 5) * i);
    }
    ctx.stroke();

    // Power-ups
    for (const p of s.powerUps) {
      const pulse = 0.85 + Math.sin(p.t * 4) * 0.2;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.t * 1.4);
      const col = p.kind === 'shield' ? '#7dfaff' : '#ffcf3a';
      ctx.shadowColor = col;
      ctx.shadowBlur = 18;
      ctx.fillStyle = col;
      ctx.globalAlpha = 0.95 * clamp(p.ttl / 3, 0.4, 1);
      ctx.fillRect(-6 * pulse, -6 * pulse, 12 * pulse, 12 * pulse);
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#0B0F1E';
      ctx.font = '700 8px system-ui';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(p.kind === 'shield' ? 'S' : 'R', 0, 0);
      ctx.restore();
    }

    // Rocks
    for (const r of s.rocks) {
      ctx.save();
      ctx.translate(r.x, r.y);
      ctx.rotate(r.angle);
      ctx.shadowColor = r.isBoss ? biome.glow : biome.accent;
      ctx.shadowBlur = r.isBoss ? 22 : 10;
      ctx.strokeStyle = r.isBoss ? biome.glow : biome.accent;
      ctx.lineWidth = r.isBoss ? 2.2 : 1.5;
      ctx.fillStyle = r.isBoss ? `${biome.glow}22` : `${biome.accent}12`;
      ctx.beginPath();
      const pts = r.shape;
      for (let i = 0; i < pts.length; i++) {
        const a = (i / pts.length) * TAU;
        const rad = r.radius * pts[i];
        const x = Math.cos(a) * rad;
        const y = Math.sin(a) * rad;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // Scouts
    for (const sc of s.scouts) {
      ctx.save();
      ctx.translate(sc.x, sc.y);
      ctx.shadowColor = '#ff5c8a';
      ctx.shadowBlur = 12;
      ctx.strokeStyle = '#ff5c8a';
      ctx.fillStyle = 'rgba(255,92,138,0.14)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(-14, 0);
      ctx.lineTo(-4, -6);
      ctx.lineTo(4, -6);
      ctx.lineTo(14, 0);
      ctx.lineTo(4, 6);
      ctx.lineTo(-4, 6);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Cockpit
      ctx.fillStyle = '#ffb0c2';
      ctx.beginPath(); ctx.arc(0, -1, 2.4, 0, TAU); ctx.fill();
      ctx.restore();
    }

    // Bullets
    for (const b of s.bullets) {
      const col = b.hostile ? '#ff5c8a' : ship.glow;
      ctx.save();
      // Outer glow halo
      ctx.shadowColor = col;
      ctx.shadowBlur = 18;
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.hostile ? 5 : 5, 0, TAU);
      ctx.fill();
      // Bright inner core (white-hot)
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.hostile ? 2.4 : 2.6, 0, TAU);
      ctx.fill();
      ctx.restore();
    }

    // Ship
    if (!s.gameOver) {
      const flicker = s.invulT > 0 ? (Math.sin(s.parallaxT * 30) > 0 ? 0.5 : 1) : 1;
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.angle + Math.PI / 2);
      ctx.globalAlpha = flicker;
      // Shield glow
      if (s.shieldT > 0) {
        ctx.save();
        ctx.shadowColor = '#7dfaff';
        ctx.shadowBlur = 24;
        ctx.strokeStyle = 'rgba(125,250,255,0.9)';
        ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.arc(0, 0, 34, 0, TAU); ctx.stroke();
        ctx.restore();
      }
      // Engine glow
      if (s.thrusting) {
        ctx.save();
        ctx.shadowColor = ship.engine;
        ctx.shadowBlur = 22;
        ctx.fillStyle = ship.engine;
        ctx.beginPath();
        ctx.moveTo(-10, 22);
        ctx.lineTo(0, 36 + Math.random() * 9);
        ctx.lineTo(10, 22);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      // Body — now ~1.6x larger for readability
      ctx.shadowColor = ship.glow;
      ctx.shadowBlur = 18;
      ctx.fillStyle = ship.body;
      ctx.strokeStyle = ship.glow;
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(0, -26);       // Nose
      ctx.lineTo(19, 19);       // Right wing
      ctx.lineTo(0, 9);         // Rear notch
      ctx.lineTo(-19, 19);      // Left wing
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Cockpit dot
      ctx.shadowBlur = 0;
      ctx.fillStyle = ship.engine;
      ctx.beginPath(); ctx.arc(0, -6, 3.4, 0, TAU); ctx.fill();
      ctx.restore();
    }

    // Particles
    for (const p of s.particles) {
      const a = p.life / p.maxLife;
      ctx.globalAlpha = clamp(a, 0, 1);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    ctx.globalAlpha = 1;

    ctx.restore();
  }

  /* ---- Start / restart ---- */
  const startPlaying = () => {
    const dim = fit();
    if (!dim) return;
    // Compose cfg
    const cfg = mode === 'campaign' ? level : synthesizeEndlessCfg(mode === 'master');
    stateRef.current = buildState(dim.w, dim.h, cfg, mode, ship);
    setHudScore(0);
    setHudLives(cfg.lives + (ship.bonusLives || 0));
    setHudRemaining(cfg.waveMass);
    setPaused(false);
    setRunResult(null);
    // Freeze the sim during the 3-2-1-GO countdown; render still runs so the
    // player sees the field settle into place.
    pausedRef.current = true;
    setView('playing');
    lastTsRef.current = 0;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
    playSfx('button', { volume: 0.3 });
    // Kick off shared Lounge countdown, then unfreeze
    beginCountdown(() => {
      pausedRef.current = false;
      lastTsRef.current = 0; // avoid a time jump
    });
  };

  const togglePause = () => {
    if (view !== 'playing') return;
    if (countdown) return; // Don't allow pause during countdown
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    playSfx('button', { volume: 0.25 });
  };

  const advanceLevel = () => {
    const nx = Math.min(MAX_LEVEL, levelId + 1);
    setLevelId(nx);
    setTimeout(startPlaying, 60);
  };

  /* ---- Endless / Master synthetic cfg ---- */
  function synthesizeEndlessCfg(master) {
    return {
      id: 0,
      biome: 6,
      name: master ? 'Master Peace' : 'Endless Peace',
      intro: master ? 'One life. The whole prism.' : 'Survive as long as you can.',
      waveMass: master ? 18 : 12,
      rockSpeed: master ? 110 : 78,
      rockSpin: 1.4,
      hasScouts: true,
      scoutCount: master ? 4 : 2,
      isBoss: false,
      lives: master ? 1 : 3,
      powerUpEvery: master ? 24 : 14,
      starThresholds: { one: 'survive', two: 'survive', three: 'survive' },
    };
  }

  /* ---- Lifecycle ---- */
  useEffect(() => {
    fit();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
      g.addColorStop(0, biome.bg[0]);
      g.addColorStop(1, biome.bg[1]);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    const onVis = () => { pausedRef.current = document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fit, biome]);

  /* ---- Keyboard input ---- */
  useEffect(() => {
    // Cancel any in-flight countdown when we leave the playing view.
    if (view !== 'playing') cancelCountdown();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view !== 'playing') return;
    const onDown = (e) => {
      if (['ArrowLeft', 'KeyA'].includes(e.code))  { e.preventDefault(); inputRef.current.rotL = true; }
      if (['ArrowRight', 'KeyD'].includes(e.code)) { e.preventDefault(); inputRef.current.rotR = true; }
      if (['ArrowUp', 'KeyW'].includes(e.code))    { e.preventDefault(); inputRef.current.thrust = true; }
      if (e.code === 'Space')                      { e.preventDefault(); inputRef.current.fire = true; }
      if (e.code === 'Escape' || e.code === 'KeyP'){ e.preventDefault(); togglePause(); }
    };
    const onUp = (e) => {
      if (['ArrowLeft', 'KeyA'].includes(e.code))  inputRef.current.rotL = false;
      if (['ArrowRight', 'KeyD'].includes(e.code)) inputRef.current.rotR = false;
      if (['ArrowUp', 'KeyW'].includes(e.code))    inputRef.current.thrust = false;
      if (e.code === 'Space')                      inputRef.current.fire = false;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
    // togglePause read via stable closure; adding it re-binds every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  /* ---- Hangar handlers ---- */
  const handleUnlockShip = (shipCfg) => {
    const res = unlockPDShip(shipCfg);
    if (res.ok) {
      equipPDShip(shipCfg.id);
      setNotice(`${shipCfg.name} unlocked & equipped`);
      playSfx('unlock', { volume: 0.4 });
    } else if (res.reason === 'not-enough-fp') {
      setNotice(`Need ${res.required} FP — earn more by playing.`);
    } else if (res.reason === 'level-locked') {
      setNotice(`Clear Level ${res.required} first.`);
    }
    setTimeout(() => setNotice(null), 2400);
  };
  const handleEquipShip = (id) => {
    equipPDShip(id);
    playSfx('button', { volume: 0.3 });
  };

  /* ------------------------------- Renders ------------------------------- */
  if (view === 'map') {
    return (
      <section ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4"
               data-testid="peace-defender">
        <div className="relative w-full aspect-[16/9] overflow-hidden"
             style={{ background: `linear-gradient(135deg, #0a0326 0%, #38104a 55%, #1a0d3a 100%)` }}>
          {/* Neon grid backdrop — shows through if the banner image is missing */}
          <div className="absolute inset-0 opacity-70"
               style={{
                 backgroundImage: `linear-gradient(rgba(125,250,255,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(255,92,255,0.10) 1px, transparent 1px)`,
                 backgroundSize: '40px 40px, 40px 40px',
                 maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
                 WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
               }}/>
          <img src="/images/lounge/peace-defender.png"
               alt="Peace Defender — Defend the peace. Break the noise. Fly quiet."
               className="absolute inset-0 w-full h-full object-cover"
               loading="lazy"
               onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
        </div>

        <div className="p-4 lg:p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold">Campaign progress</p>
              <p className="font-heading text-lg text-navy mt-0.5">
                {Math.max(0, (pd?.highestUnlocked ?? 1) - 1)} of {MAX_LEVEL} cleared ·{' '}
                <span className="text-gold">
                  {Object.values(pd?.levelStars ?? {}).reduce((a, b) => a + b, 0)}
                </span> / {MAX_LEVEL * 3} stars
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setSfxMuted(!muted)} data-testid="pd-mute"
                      className="rounded-full w-9 h-9 flex items-center justify-center border border-navy/10 text-navy/70 hover:text-navy hover:border-navy/25 transition-smooth"
                      aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
              </button>
              <button type="button" onClick={() => { setLevelId(pd?.lastLevel || 1); setMode('campaign'); setView('ready'); scrollSectionIntoView(); }}
                      data-testid="pd-resume"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white font-semibold text-sm hover:brightness-110 transition-smooth">
                Resume Level {pd?.lastLevel || 1} <ChevronRight size={14}/>
              </button>
            </div>
          </div>

          <PeaceDefenderLevelSelect
            pd={pd}
            fp={fp}
            onPick={(id) => { setLevelId(id); setMode('campaign'); setView('ready'); playSfx('button', { volume: 0.3 }); scrollSectionIntoView(); }}
            onEndless={() => { setMode('endless'); setView('ready'); playSfx('unlock', { volume: 0.3 }); scrollSectionIntoView(); }}
            onMaster={() => { setMode('master'); setView('ready'); playSfx('unlock', { volume: 0.3 }); scrollSectionIntoView(); }}
            onUnlockShip={handleUnlockShip}
            onEquipShip={handleEquipShip}
          />

          {notice && (
            <p className="text-center text-xs px-3 py-2 rounded-full inline-block mx-auto text-white font-semibold"
               style={{ background: 'rgba(11,25,44,0.9)' }} data-testid="pd-notice">
              {notice}
            </p>
          )}

          <p className="text-center text-xs text-navy/40 leading-relaxed">
            Clear a wave to unlock the next — replay to raise your stars and unlock ships in the Hangar.
          </p>
        </div>
      </section>
    );
  }

  const currentBest = mode === 'endless' ? (pd?.endlessBest ?? 0) : mode === 'master' ? (pd?.masterBest ?? 0) : (pd?.levelBest?.[levelId]?.hits ?? null);

  return (
    <section ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4" data-testid="peace-defender">
      {/* HUD strip */}
      <div className="px-4 lg:px-5 pt-4 pb-2 flex items-center justify-between gap-3">
        <button type="button" onClick={() => setView('map')} data-testid="pd-back-map"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth">
          ← Level select
        </button>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.28em] text-navy/40 font-semibold">
            {mode === 'campaign' ? `Biome ${biome.id} · ${biome.name}` : mode === 'endless' ? 'Endless Peace' : 'Master Peace'}
          </p>
          <p className="font-heading text-base text-navy mt-0.5">
            {mode === 'campaign' ? `Level ${levelId} — ${level.name}` : (mode === 'endless' ? 'Survive as long as you can' : 'One life. The whole prism.')}
          </p>
        </div>
        <button type="button" onClick={() => setView('map')} data-testid="pd-home"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth"
                aria-label="Back to level select">
          <Home size={13}/>
        </button>
      </div>

      <div className="px-4 lg:px-5 pb-4">
        <div ref={wrapperRef} className="relative rounded-2xl overflow-hidden border border-navy/10 select-none">
          <canvas ref={canvasRef} className={`block w-full touch-none ${view === 'ready' ? 'hidden' : ''}`} data-testid="pd-canvas"/>

          {/* HUD overlay */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none z-10">
            <div className="flex items-center gap-2">
              <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Kills</p>
                <p className="font-heading text-2xl text-white leading-none mt-0.5 tabular-nums" data-testid="pd-score">{hudScore}</p>
              </div>
              <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Lives</p>
                <p className="font-heading text-2xl leading-none mt-0.5 tabular-nums" data-testid="pd-lives"
                   style={{ color: hudLives >= 2 ? '#7dfaff' : hudLives === 1 ? '#ffcf3a' : '#ff5c8a' }}>
                  {hudLives}
                </p>
              </div>
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Wave</p>
                  <p className="font-heading text-2xl text-white leading-none mt-0.5 tabular-nums" data-testid="pd-remaining">{hudRemaining}</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-gold/25 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <div className="mt-0.5"><StarBadge count={bestStars} size={11} testid="pd-hud-best-stars"/></div>
                </div>
              )}
              {mode !== 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/45 backdrop-blur-md border border-gold/25 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <p className="font-heading text-2xl text-gold leading-none mt-0.5 tabular-nums" data-testid="pd-hud-best">{currentBest ?? 0}</p>
                </div>
              )}
            </div>
          </div>

          {/* Mobile touch controls */}
          {view === 'playing' && !paused && (
            <MobileControls inputRef={inputRef}/>
          )}

          {/* 3-2-1-GO countdown (shared across all Lounge games) */}
          <LoungeCountdown n={countdown} testid="pd-countdown" color="#7dfaff" />

          {/* Pause button */}
          {view === 'playing' && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 pointer-events-auto z-10 mt-24 sm:mt-0 sm:top-auto sm:bottom-3">
              <button onClick={togglePause} data-testid="pd-pause"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-navy bg-gold hover:brightness-95 transition-smooth shadow">
                {paused ? <><Play size={12}/> Resume</> : <><Pause size={12}/> Pause</>}
              </button>
            </div>
          )}

          {/* Paused overlay */}
          {view === 'playing' && paused && (
            <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-5 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.9) 0%, rgba(58,15,122,0.9) 100%)' }}
                 data-testid="pd-paused">
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: '#7dfaff' }}>Paused</p>
              <p className="font-heading text-2xl mt-2">Take a breath</p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={togglePause} data-testid="pd-pause-resume"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-300 text-navy font-semibold hover:brightness-95 text-sm">
                  <Play size={14}/> Resume
                </button>
                <button onClick={() => { cancelCountdown(); setView('map'); }} data-testid="pd-pause-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/8 border border-white/15 text-white hover:bg-white/15 text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}

          {view === 'ready' && (
            <div className="relative w-full backdrop-blur-md flex flex-col items-center text-white z-20 px-5 py-8 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.92) 0%, rgba(58,15,122,0.94) 100%)' }}
                 data-testid="pd-ready">
              <div className="my-auto w-full flex flex-col items-center py-6">
              <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: biome.accent }}>
                {mode === 'campaign' ? `Level ${levelId} · ${biome.name}` : mode === 'endless' ? 'Endless Peace' : 'Master Peace'}
              </p>
              <p className="font-heading text-3xl lg:text-4xl mt-2 max-w-lg leading-tight">
                {mode === 'campaign' ? level.name : (mode === 'endless' ? 'Survive as long as you can' : 'One life. The whole prism.')}
              </p>
              {mode === 'campaign' && (
                <p className="mt-3 text-sm text-white/80 max-w-md italic">{level.intro}</p>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-white/75">
                {mode === 'campaign' && (
                  <>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10 inline-flex items-center gap-1">
                      <Rocket size={10} className="text-cyan-300"/> {ship.name}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10">
                      Lives · <span className="text-cyan-300 tabular-nums">{(level.lives) + (ship.bonusLives || 0)}</span>
                    </span>
                    {level.isBoss && (
                      <span className="px-2.5 py-1 rounded-full text-navy font-semibold" style={{ background: biome.glow }}>
                        BOSS WAVE
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="mt-4 text-xs text-white/60 leading-relaxed max-w-sm">
                <p>
                  <span className="text-cyan-300 font-semibold">Rotate</span> ← → ·{' '}
                  <span className="text-cyan-300 font-semibold">Thrust</span> ↑ ·{' '}
                  <span className="text-cyan-300 font-semibold">Fire</span> Space
                </p>
                <p className="mt-1 opacity-80">Mobile: joystick + fire on right</p>
              </div>
              <button onClick={startPlaying} data-testid="pd-start"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-300 text-navy font-semibold hover:brightness-95 transition-smooth">
                <Play size={15}/> Start
              </button>
              </div>
            </div>
          )}

          {view === 'over' && runResult && (
            <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-5 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.94) 0%, rgba(58,15,122,0.95) 100%)' }}
                 data-testid="pd-over">
              <p className={`text-[10px] uppercase tracking-[0.32em] font-semibold ${runResult.cleared ? 'text-cyan-300' : 'text-red-300'}`}>
                {runResult.cleared ? (mode === 'campaign' ? `Wave ${levelId} cleared` : 'Nice run') : (mode === 'campaign' ? 'Ship lost' : 'End of run')}
              </p>
              {!runResult.cleared && mode === 'campaign' && (
                <p className="text-xs text-white/60 mt-2 max-w-xs">Break every rock in the wave to earn stars. Fewer hits = more stars.</p>
              )}
              {mode === 'campaign' && runResult.cleared && (
                <div className="mt-3"><StarBadge count={runResult.stars} size={30} testid="pd-over-stars"/></div>
              )}
              <p className="font-heading text-3xl lg:text-4xl mt-3">
                {runResult.score} <span className="text-white/50 text-base">kills</span>
              </p>
              <p className="text-xs text-white/60 mt-1">
                {runResult.hits === 0 ? 'Flawless run' : `${runResult.hits} hit${runResult.hits > 1 ? 's' : ''}`}
                {runResult.tokens > 0 ? ` · ${runResult.tokens} power-up${runResult.tokens > 1 ? 's' : ''}` : ''}
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={startPlaying} data-testid="pd-retry"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-300 text-navy font-semibold hover:brightness-95 text-sm">
                  <RotateCcw size={14}/> Play again
                </button>
                {mode === 'campaign' && runResult.cleared && levelId < MAX_LEVEL && (
                  <button onClick={advanceLevel} data-testid="pd-next-level"
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/90 text-navy font-semibold hover:brightness-95 text-sm">
                    <Sparkles size={14}/> Next level
                  </button>
                )}
                <button onClick={() => setView('map')} data-testid="pd-over-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/8 border border-white/15 text-white hover:bg-white/15 text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------
 * MobileControls — thumb-friendly touch pads (Left = rotate/thrust,
 * Right = fire). Renders only during active play.
 * ------------------------------------------------------------------ */
function MobileControls({ inputRef }) {
  const setInput = (patch) => Object.assign(inputRef.current, patch);

  const holdLeft  = () => setInput({ rotL: true, rotR: false });
  const holdRight = () => setInput({ rotL: false, rotR: true });
  const holdThr   = () => setInput({ thrust: true });
  const holdFire  = () => setInput({ fire: true });
  const releaseAll = () => setInput({ rotL: false, rotR: false, thrust: false });
  const releaseFire = () => setInput({ fire: false });

  const btn = 'select-none touch-none rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-white font-bold flex items-center justify-center active:bg-black/60 active:scale-95 transition-transform';

  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      {/* Left pad */}
      <div className="absolute bottom-3 left-3 flex flex-col items-center gap-1.5 pointer-events-auto">
        <button className={`${btn} w-11 h-11`} data-testid="pd-mobile-thrust"
                onPointerDown={holdThr} onPointerUp={releaseAll} onPointerLeave={releaseAll} onPointerCancel={releaseAll}>
          ▲
        </button>
        <div className="flex items-center gap-1.5">
          <button className={`${btn} w-11 h-11`} data-testid="pd-mobile-rot-left"
                  onPointerDown={holdLeft} onPointerUp={releaseAll} onPointerLeave={releaseAll} onPointerCancel={releaseAll}>
            ◀
          </button>
          <button className={`${btn} w-11 h-11`} data-testid="pd-mobile-rot-right"
                  onPointerDown={holdRight} onPointerUp={releaseAll} onPointerLeave={releaseAll} onPointerCancel={releaseAll}>
            ▶
          </button>
        </div>
      </div>
      {/* Right fire */}
      <div className="absolute bottom-3 right-3 pointer-events-auto">
        <button className={`${btn} w-16 h-16 text-lg`} data-testid="pd-mobile-fire"
                onPointerDown={holdFire} onPointerUp={releaseFire} onPointerLeave={releaseFire} onPointerCancel={releaseFire}>
          ●
        </button>
      </div>
    </div>
  );
}
